import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import {
  InAppNotificationDto,
  InAppNotificationFeedDto,
  NotificationApiService,
  NotificationAudienceMode,
} from './notification-api.service';
import { InAppNotificationPushDto, NotificationsHubService } from './notifications-hub.service';

/**
 * Shared state for the topbar notification bell.
 * Load once on auth; live-update via SignalR — no polling.
 */
@Injectable({
  providedIn: 'root',
})
export class InAppNotificationsStateService implements OnDestroy {
  private readonly api = inject(NotificationApiService);
  private readonly hub = inject(NotificationsHubService);

  private readonly destroy$ = new Subject<void>();
  private unsubHub: (() => void) | null = null;
  private unsubReconnect: (() => void) | null = null;
  private mode: NotificationAudienceMode = 'staff';
  private subscribedMarinaId: string | null = null;
  private subscribedCustomerId: string | null = null;

  /**
   * Bumps on every start/stop so a late `stop()` cannot clear items after a newer
   * `start*()`, and stale feed responses cannot overwrite newer state.
   */
  private sessionEpoch = 0;
  private refreshEpoch = 0;
  /** Bumps when unread count is changed locally (push / mark read). Stale feed refresh must not overwrite. */
  private unreadMutationEpoch = 0;
  private startChain: Promise<void> = Promise.resolve();

  private readonly itemsSubject = new BehaviorSubject<InAppNotificationDto[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly toastSubject = new Subject<InAppNotificationDto>();

  readonly items$ = this.itemsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  /** Fires for each newly pushed notification (live receive only; not feed refresh). */
  readonly toast$ = this.toastSubject.asObservable();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    void this.teardown();
  }

  async startStaff(marinaId: string): Promise<void> {
    return this.enqueueStart(async () => {
      const epoch = ++this.sessionEpoch;
      this.mode = 'staff';
      await this.hub.connect();
      if (epoch !== this.sessionEpoch) return;

      if (this.subscribedMarinaId && this.subscribedMarinaId !== marinaId) {
        await this.hub.unsubscribeFromMarina(this.subscribedMarinaId);
      }
      if (epoch !== this.sessionEpoch) return;

      await this.hub.subscribeToMarina(marinaId);
      if (epoch !== this.sessionEpoch) return;

      this.subscribedMarinaId = marinaId;
      this.ensureHubHandler();
      await this.refresh(epoch);
    });
  }

  async startCustomer(customerId: string): Promise<void> {
    return this.enqueueStart(async () => {
      const epoch = ++this.sessionEpoch;
      this.mode = 'customer';
      await this.hub.connect();
      if (epoch !== this.sessionEpoch) return;

      if (this.subscribedCustomerId && this.subscribedCustomerId !== customerId) {
        await this.hub.unsubscribeFromCustomer(this.subscribedCustomerId);
      }
      if (epoch !== this.sessionEpoch) return;

      await this.hub.subscribeToCustomer(customerId);
      if (epoch !== this.sessionEpoch) return;

      this.subscribedCustomerId = customerId;
      this.ensureHubHandler();
      await this.refresh(epoch);
    });
  }

  async stop(): Promise<void> {
    const epoch = ++this.sessionEpoch;
    await this.teardown();
    // A newer start* won the race — leave its feed alone.
    if (epoch !== this.sessionEpoch) return;
    this.itemsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.unreadMutationEpoch = 0;
  }

  async refresh(forSessionEpoch?: number): Promise<void> {
    const sessionAtStart = forSessionEpoch ?? this.sessionEpoch;
    const unreadMutationAtStart = this.unreadMutationEpoch;
    const epoch = ++this.refreshEpoch;
    this.loadingSubject.next(true);
    try {
      const feed = await firstValueFrom(this.api.getFeed(this.mode, 20));
      if (epoch !== this.refreshEpoch || sessionAtStart !== this.sessionEpoch) return;
      this.applyFeed(feed, unreadMutationAtStart);
    } catch (e) {
      console.warn('[InAppNotificationsStateService] Failed to load feed', e);
    } finally {
      if (epoch === this.refreshEpoch) {
        this.loadingSubject.next(false);
      }
    }
  }

  async markRead(notification: InAppNotificationDto): Promise<void> {
    if (!notification.isRead) {
      try {
        await firstValueFrom(this.api.markRead(this.mode, notification.id));
      } catch (e) {
        console.warn('[InAppNotificationsStateService] markRead failed', e);
      }
    }

    const items = this.itemsSubject.value.map((n) =>
      n.id === notification.id ? { ...n, isRead: true } : n
    );
    this.itemsSubject.next(items);
    if (!notification.isRead) {
      this.noteUnreadMutation();
      this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
    }
  }

  async markAllRead(): Promise<void> {
    try {
      await firstValueFrom(this.api.markAllRead(this.mode));
    } catch (e) {
      console.warn('[InAppNotificationsStateService] markAllRead failed', e);
      return;
    }
    this.noteUnreadMutation();
    this.itemsSubject.next(this.itemsSubject.value.map((n) => ({ ...n, isRead: true })));
    this.unreadCountSubject.next(0);
  }

  private applyFeed(feed: InAppNotificationFeedDto, unreadMutationAtStart?: number): void {
    this.itemsSubject.next(feed.items ?? []);
    // A feed fetch started before mark-all-read or a live push must not restore a stale total/unread count.
    if (
      unreadMutationAtStart !== undefined &&
      unreadMutationAtStart !== this.unreadMutationEpoch
    ) {
      return;
    }
    this.unreadCountSubject.next(feed.unreadCount ?? 0);
  }

  private noteUnreadMutation(): void {
    this.unreadMutationEpoch++;
  }

  private ensureHubHandler(): void {
    if (!this.unsubHub) {
      this.unsubHub = this.hub.onNotificationCreated((dto) => this.onPush(dto));
    }
    if (!this.unsubReconnect) {
      this.unsubReconnect = this.hub.onReconnected(() => {
        void this.refresh();
      });
    }
  }

  private onPush(dto: InAppNotificationPushDto): void {
    const existing = this.itemsSubject.value;
    if (existing.some((n) => n.id === dto.id)) return;

    const item: InAppNotificationDto = {
      id: dto.id,
      marinaId: dto.marinaId,
      audience: dto.audience,
      customerId: dto.customerId,
      type: dto.type,
      title: dto.title,
      message: dto.message,
      entityType: dto.entityType,
      entityId: dto.entityId,
      deepLink: dto.deepLink,
      createdAt: dto.createdAt,
      isRead: false,
    };

    this.itemsSubject.next([item, ...existing].slice(0, 20));
    this.toastSubject.next(item);

    this.noteUnreadMutation();
    // Always increment locally — never use dto.unreadCount (not per-user for staff; not reliable for customer).
    this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
  }

  private enqueueStart(work: () => Promise<void>): Promise<void> {
    const run = this.startChain.then(work, work);
    // Keep the chain alive even if a start fails.
    this.startChain = run.catch(() => undefined);
    return run;
  }

  private async teardown(): Promise<void> {
    if (this.unsubHub) {
      this.unsubHub();
      this.unsubHub = null;
    }
    if (this.unsubReconnect) {
      this.unsubReconnect();
      this.unsubReconnect = null;
    }
    if (this.subscribedMarinaId) {
      await this.hub.unsubscribeFromMarina(this.subscribedMarinaId).catch(() => {});
      this.subscribedMarinaId = null;
    }
    if (this.subscribedCustomerId) {
      await this.hub.unsubscribeFromCustomer(this.subscribedCustomerId).catch(() => {});
      this.subscribedCustomerId = null;
    }
    await this.hub.disconnect().catch(() => {});
  }
}
